import type { IconName } from '../../components/ui';

interface DocPage {
  /** Empty for overview. */
  slug: string;
  title: string;
  nav: string;
  group: string;
  icon: IconName;
  /** One-line card text. */
  blurb: string;
  /** Extra search terms. */
  keywords: string;
  /** Searchable headings: [id, title]. */
  sections: Array<[string, string]>;
}

export const GROUPS = ['Memulai', 'Teks', 'Gambar dan video', 'Integrasi', 'Referensi'] as const;

export const PAGES: DocPage[] = [
  {
    slug: '',
    title: 'Dokumentasi API',
    nav: 'Mulai cepat',
    group: 'Memulai',
    icon: 'bolt',
    blurb: 'Request pertama dalam tiga langkah.',
    keywords: 'base url install sdk openai quickstart request pertama',
    sections: [['langkah', 'Mulai dalam 3 langkah'], ['topik', 'Jelajahi topik']],
  },
  {
    slug: 'autentikasi',
    title: 'Autentikasi',
    nav: 'Autentikasi',
    group: 'Memulai',
    icon: 'key',
    blurb: 'Cara memakai dan menyimpan API key.',
    keywords: 'api key bearer authorization header aia_live aia_test allowed_models model_not_allowed 401 403',
    sections: [
      ['header', 'Kirim key di header'],
      ['cek', 'Cek key Anda'],
      ['batasi-model', 'Membatasi model per API key'],
      ['hak-akses', 'API key dan login portal'],
    ],
  },
  {
    slug: 'model',
    title: 'Memilih model',
    nav: 'Memilih model',
    group: 'Memulai',
    icon: 'grid',
    blurb: 'Daftar model, router auto, dan model cadangan.',
    keywords: 'katalog harga konteks auto router fallback_models cadangan /v1/models',
    sections: [
      ['katalog', 'Model yang tersedia'],
      ['auto', 'Biarkan router memilih'],
      ['fallback', 'Model cadangan'],
    ],
  },
  {
    slug: 'chat',
    title: 'Chat dan streaming',
    nav: 'Chat dan streaming',
    group: 'Teks',
    icon: 'chat',
    blurb: 'Kirim pesan, terima jawaban, sekaligus atau bertahap.',
    keywords: 'chat completions messages system prompt stream sse temperature max_tokens top_p reasoning_effort usage',
    sections: [
      ['request', 'Kirim request'],
      ['parameter', 'Parameter'],
      ['parameter-umum', 'Parameter yang sering dipakai'],
      ['respons', 'Respons'],
      ['streaming', 'Streaming'],
    ],
  },
  {
    slug: 'tools',
    title: 'Tool calling dan JSON',
    nav: 'Tool calling dan JSON',
    group: 'Teks',
    icon: 'braces',
    blurb: 'Model memanggil fungsi Anda, atau menjawab dalam JSON.',
    keywords: 'function calling fungsi tools tool_choice json schema response_format structured output',
    sections: [
      ['tool-calling', 'Tool calling'],
      ['json', 'Output JSON'],
    ],
  },
  {
    slug: 'anthropic',
    title: 'Format Anthropic (Messages)',
    nav: 'Format Anthropic',
    group: 'Teks',
    icon: 'chat',
    blurb: 'Endpoint /v1/messages untuk Claude Code dan SDK Anthropic.',
    keywords:
      'anthropic messages api /v1/messages count_tokens x-api-key anthropic-version claude sdk thinking adaptive budget_tokens cache_control prompt caching ephemeral pdf document tool_use tool_result',
    sections: [
      ['endpoint', 'Endpoint'],
      ['contoh', 'Contoh request'],
      ['fitur', 'Yang didukung'],
      ['caching', 'Prompt caching'],
      ['batasan', 'Batasan'],
    ],
  },
  {
    slug: 'responses',
    title: 'Responses API',
    nav: 'Responses API',
    group: 'Teks',
    icon: 'braces',
    blurb: 'Endpoint /v1/responses untuk Codex dan OpenAI Agents SDK.',
    keywords:
      'responses api /v1/responses openai agents sdk codex input instructions function_call function_call_output custom tool namespace reasoning summary input_tokens store previous_response_id',
    sections: [
      ['endpoint', 'Endpoint'],
      ['contoh', 'Contoh request'],
      ['fitur', 'Yang didukung'],
      ['batasan', 'Batasan'],
    ],
  },
  {
    slug: 'embeddings',
    title: 'Embeddings',
    nav: 'Embeddings',
    group: 'Teks',
    icon: 'grid',
    blurb: 'Ubah teks jadi vektor untuk pencarian, RAG, dan indeks kode.',
    keywords: 'embeddings embedding vektor vector rag pencarian semantik similarity text-embedding-3 gemini-embedding dimensions encoding_format base64 indeks kode',
    sections: [
      ['request', 'Kirim request'],
      ['parameter', 'Parameter'],
      ['respons', 'Respons'],
      ['model', 'Model embedding'],
    ],
  },
  {
    slug: 'completions',
    title: 'Completions (autocomplete)',
    nav: 'Completions',
    group: 'Teks',
    icon: 'code',
    blurb: 'Endpoint /v1/completions lama, termasuk isi-tengah (FIM).',
    keywords: 'completions legacy /v1/completions prompt suffix fim fill in the middle autocomplete tab kode text_completion',
    sections: [
      ['request', 'Kirim request'],
      ['parameter', 'Parameter'],
      ['catatan', 'Catatan'],
    ],
  },
  {
    slug: 'gambar',
    title: 'Gambar',
    nav: 'Gambar',
    group: 'Gambar dan video',
    icon: 'image',
    blurb: 'Buat atau edit gambar dalam satu request.',
    keywords: 'images generations edits ukuran size seedream b64_json url',
    sections: [
      ['request', 'Kirim request'],
      ['parameter', 'Parameter'],
      ['respons', 'Respons'],
    ],
  },
  {
    slug: 'video',
    title: 'Video',
    nav: 'Video',
    group: 'Gambar dan video',
    icon: 'video',
    blurb: 'Buat job, tunggu, lalu unduh hasilnya.',
    keywords: 'videos job polling status seedance durasi resolusi unduh',
    sections: [
      ['cara-kerja', 'Cara kerja'],
      ['contoh', 'Buat, tunggu, unduh'],
      ['parameter', 'Parameter'],
      ['objek', 'Objek video'],
      ['biaya', 'Biaya video'],
    ],
  },
  {
    slug: 'integrasi',
    title: 'Integrasi',
    nav: 'Semua integrasi',
    group: 'Integrasi',
    icon: 'plug',
    blurb: 'Satu API key untuk Claude Code, Codex, Cline, SDK, dan alat lain.',
    keywords:
      'integrasi integration compatible kompatibel openai-compatible openrouter alternatif ekstensi extension vscode vs code plugin sdk langchain llamaindex vercel ai sdk cursor continue aider zed n8n open webui librechat anthropic sdk agents sdk base url',
    sections: [
      ['format', 'Tiga format, satu API key'],
      ['panduan', 'Panduan per alat'],
      ['contoh', 'Contoh konfigurasi'],
      ['alat-lain', 'Alat lain'],
    ],
  },
  {
    slug: 'claude-code',
    title: 'Claude Code',
    nav: 'Claude Code',
    group: 'Integrasi',
    icon: 'code',
    blurb: 'Pakai Claude Code di terminal dan VS Code dengan saldo Partner AI.',
    keywords:
      'claude code claude-code cli terminal anthropic_base_url anthropic_auth_token anthropic_api_key anthropic_model haiku sonnet opus settings.json agen agent coding vscode jetbrains',
    sections: [
      ['pasang', 'Sambungkan'],
      ['model', 'Pilih model'],
      ['fitur', 'Yang didukung'],
      ['biaya', 'Biaya'],
      ['masalah', 'Kalau ada masalah'],
    ],
  },
  {
    slug: 'codex',
    title: 'Codex CLI',
    nav: 'Codex',
    group: 'Integrasi',
    icon: 'code',
    blurb: 'Pakai Codex dari OpenAI lewat Responses API Partner AI.',
    keywords: 'codex openai codex cli config.toml model_provider wire_api responses profile agen agent coding terminal',
    sections: [
      ['pasang', 'Sambungkan'],
      ['model', 'Pilih model'],
      ['fitur', 'Yang didukung'],
      ['masalah', 'Kalau ada masalah'],
    ],
  },
  {
    slug: 'cline',
    title: 'Cline, Roo Code, Kilo Code',
    nav: 'Cline, Roo, Kilo',
    group: 'Integrasi',
    icon: 'plug',
    blurb: 'Ekstensi VS Code untuk coding dengan agen AI.',
    keywords: 'cline roo code roocode kilo code kilocode ekstensi extension vscode vs code openai compatible api provider base url model id agen coding',
    sections: [
      ['pasang', 'Sambungkan'],
      ['claude', 'Model Claude: jalur Anthropic'],
      ['pengaturan', 'Pengaturan model'],
      ['masalah', 'Kalau ada masalah'],
    ],
  },
  {
    slug: 'biaya',
    title: 'Biaya dan batas',
    nav: 'Biaya dan batas',
    group: 'Referensi',
    icon: 'coin',
    blurb: 'Cara saldo dipotong, anggaran, dan rate limit.',
    keywords: 'saldo rupiah billing tagihan anggaran budget rate limit 429 rpm kuota',
    sections: [
      ['saldo', 'Cara saldo dipotong'],
      ['anggaran', 'Anggaran'],
      ['rate-limit', 'Rate limit'],
    ],
  },
  {
    slug: 'error',
    title: 'Kode error',
    nav: 'Kode error',
    group: 'Referensi',
    icon: 'warn',
    blurb: 'Arti setiap kode dan kapan aman diulang.',
    keywords: '400 401 402 403 404 409 429 500 503 insufficient_balance rate_limited retry ulang',
    sections: [
      ['bentuk', 'Bentuk error'],
      ['daftar', 'Daftar kode'],
    ],
  },
  {
    slug: 'faq',
    title: 'Pertanyaan umum',
    nav: 'Pertanyaan umum',
    group: 'Referensi',
    icon: 'help',
    blurb: 'Pembayaran, data, dan hal yang sering ditanyakan.',
    keywords: 'qris pembayaran privasi data pertanyaan kurs dolar naik harga terkunci',
    sections: [['harga-terkunci', 'Kurs dolar naik']],
  },
];

export function docPath(slug: string): string {
  return slug ? `/docs/${slug}` : '/docs';
}

/** Old one-page anchors. */
export const LEGACY_HASH: Record<string, string> = {
  mulai: '/docs',
  autentikasi: '/docs/autentikasi',
  chat: '/docs/chat',
  streaming: '/docs/chat#streaming',
  model: '/docs/model',
  'daftar-model': '/docs/model#katalog',
  tools: '/docs/tools',
  json: '/docs/tools#json',
  gambar: '/docs/gambar',
  video: '/docs/video',
  biaya: '/docs/biaya',
  batas: '/docs/biaya#rate-limit',
  error: '/docs/error',
  integrasi: '/docs/integrasi',
  'claude-code': '/docs/claude-code',
  codex: '/docs/codex',
  cline: '/docs/cline',
  faq: '/docs/faq',
};
